import * as THREE from "three";
import { BLOCKS, BLOCK_KEYS, ITEMS, PLANT_LOOKS, TILES, iconTile, type BlockKey, type Family, type ItemId, type PlantLook, type TileId, type ToolKind, type ToolTier } from "@dyson/shared";
import { SHIP_PAINT } from "./shipArt";
import { PLANT_PAINT } from "./plantArt";
import { GROUND_PAINT } from "./groundArt";
import { BUILD_PAINT } from "./buildArt";
import { S, type Ctx, VARIANTS, WOOD, WALL, IRON, BRASS, GLASS, ROPE, TEAL, hash, TURN, noise, tone, grain, type PixelFn, pixels, fill, shape, disc, ring, line, path, poly, panel, plate, brass, beam, planks, rivet, glassPane, flip, backWall, backWalls, outline } from "./paint";

export * from "./paint";

/** Clumps of leaves, each a ball lit from the upper left: [x, y, r] (mirrored by variant), later ones in front. Clear between. */
function leafClumps(v: number, list: readonly (readonly [number, number, number])[], ramp: readonly string[], seed: number): PixelFn {
  const placed = list.map(([x, y, r]) => [...flip(v, x, y), r] as const);
  return (x, y) => {
    for (let n = placed.length - 1; n >= 0; n--) {
      const [cx, cy, r] = placed[n]!;
      const dx = (x + 0.5 - cx) / r;
      const dy = (y + 0.5 - cy) / r;
      const d2 = dx * dx + dy * dy + (noise(x, y, 4, seed) - 0.5) * 0.35;
      if (d2 > 1) continue;
      // Leaves: a little dark notch here and there, so a clump isn't a smooth ball.
      if (d2 > 0.55 && hash(x, y, seed + n) < 0.2) return ramp[0]!;
      const k = 0.5 - dx * 0.3 - dy * 0.35 + Math.sqrt(Math.max(0, 1 - d2)) * 0.2 + (hash(x, y, seed + 1) - 0.5) * 0.15;
      return tone(ramp, k, x, y);
    }
    return null;
  };
}

// ----- built blocks


// ----- pieces, painted whole (see BlockSpec.piece)

/**
 * The whole turret, 4 tiles square (canvas y down: the housing is
 * the top half): an armoured iron dome over a seat in an iron frame, the gun
 * mount rising between them. Each of its tiles paints its own slice of this.
 */
/** A workbench, 3x2: a bench top on trestle legs with a vice, a saw and a hammer hanging behind. */
function paintWorkbench(ctx: Ctx): void {
  backWalls(ctx, 3, 2);
  fill(ctx, IRON[1], 18, 4, 2, 2); // pegs
  fill(ctx, IRON[1], 61, 3, 2, 2);
  beam(ctx, 7, 6, 6, 18); // saw handle
  fill(ctx, WOOD[0], 9, 11, 2, 6);
  poly(ctx, "#b8bcc2", [13, 8, 36, 10, 36, 18, 13, 21]);
  line(ctx, "#e4e8ec", 13, 8.5, 36, 10.5, 1);
  for (let x = 14; x < 36; x += 2) fill(ctx, IRON[2], x, 20 - Math.floor(((x - 13) / 23) * 3), 1, 1);
  beam(ctx, 60, 6, 4, 20); // hammer
  panel(ctx, 52, 4, 20, 7, IRON[3], IRON[4], IRON[1]);
  beam(ctx, 30, 22, 20, 6); // an offcut on the bench
  fill(ctx, WOOD[4], 48, 22, 2, 6);
  beam(ctx, 2, 28, 92, 8); // bench top
  fill(ctx, WOOD[0], 2, 36, 92, 1);
  panel(ctx, 74, 18, 14, 10, IRON[2], IRON[3], IRON[0]); // vice
  fill(ctx, IRON[4], 69, 22, 5, 2);
  disc(ctx, IRON[3], 68, 23, 2);
  beam(ctx, 8, 37, 6, 27); // legs
  beam(ctx, 82, 37, 6, 27);
  beam(ctx, 14, 50, 68, 5); // stretcher
}

/** A shut hatch, 2x4: a stout plank door in an iron frame, with a porthole and a latch. */
function paintHatch(ctx: Ctx): void {
  planks(ctx, 0, 0, 64, 128, 13, true, 66);
  plate(ctx, 0, 0, 6, 128); // frame
  plate(ctx, 58, 0, 6, 128);
  plate(ctx, 0, 0, 64, 6);
  for (const y of [78, 110]) {
    plate(ctx, 6, y, 52, 6); // iron bands
    for (const x of [10, 30, 50]) rivet(ctx, x, y + 2);
  }
  disc(ctx, IRON[0], 32, 36, 17); // porthole
  disc(ctx, IRON[3], 32, 36, 16);
  disc(ctx, IRON[2], 33, 37, 14);
  glassPaneDisc(ctx, 32, 36, 11);
  for (const a of [0, 1, 2, 3, 4, 5, 6, 7]) rivet(ctx, 31 + Math.round(Math.cos((a * Math.PI) / 4) * 14) - 1, 35 + Math.round(Math.sin((a * Math.PI) / 4) * 14) - 1);
  brass(ctx, 46, 62, 8, 8); // latch
  fill(ctx, BRASS[0], 49, 65, 2, 2);
}

/** A round window of glass: dark at the rim, a streak of reflection. */
export function glassPaneDisc(ctx: Ctx, cx: number, cy: number, r: number): void {
  pixels(
    ctx,
    r * 2,
    r * 2,
    (x, y) => {
      const dx = x + 0.5 - r;
      const dy = y + 0.5 - r;
      if (dx * dx + dy * dy > r * r) return null;
      const d = dx + dy;
      if ((d > -6 && d < -3) || (d > 0 && d < 1.5)) return GLASS[4];
      return tone(GLASS, 0.75 - (dy / r) * 0.3 - Math.hypot(dx, dy) / r * 0.25, x, y);
    },
    cx - r,
    cy - r,
  );
}

/** A dark way through, darkest in the middle: what's behind an open door or hatch. */
export function opening(ctx: Ctx, w: number, h: number): void {
  pixels(ctx, w, h, (x, y) => tone(["#0b0805", "#120c07", "#1a120a", "#231910"], 0.1 + (Math.abs(x + 0.5 - w / 2) / (w / 2)) * 0.6 + (noise(x, y, 8, 3) - 0.5) * 0.2, x, y));
}

/** An open hatch, 2x4: the door swung back against the frame, a dark way through. */
function paintHatchOpen(ctx: Ctx): void {
  opening(ctx, 64, 128);
  plate(ctx, 0, 0, 6, 128);
  plate(ctx, 58, 0, 6, 128);
  plate(ctx, 0, 0, 64, 6);
  fill(ctx, "#0b0805", 6, 6, 52, 3); // shadow under the frame
  beam(ctx, 6, 6, 10, 122); // the door, edge on
  fill(ctx, IRON[2], 6, 78, 10, 6);
  fill(ctx, IRON[2], 6, 110, 10, 6);
}

/** A chest, 2x2: an iron-bound wooden box with a brass lock. */
function paintChest(ctx: Ctx): void {
  backWalls(ctx, 2, 2);
  planks(ctx, 4, 36, 56, 28, 7, false, 84); // box
  planks(ctx, 4, 22, 56, 14, 7, false, 85, ["#4a2f17", "#6e4622", "#9a6834", "#b88049", "#d09858"]); // lid
  fill(ctx, WOOD[0], 4, 36, 56, 2);
  fill(ctx, WOOD[0], 6, 21, 52, 1);
  for (const x of [8, 50]) {
    plate(ctx, x, 21, 6, 43); // iron bands
    rivet(ctx, x + 2, 26);
    rivet(ctx, x + 2, 56);
  }
  brass(ctx, 27, 32, 10, 11); // lock
  fill(ctx, "#1a1410", 31, 36, 2, 4);
}

/** A bed, 4x2: a timber frame with a headboard, a straw mattress, a pillow and a blue blanket. */
function paintBed(ctx: Ctx): void {
  backWalls(ctx, 4, 2);
  beam(ctx, 2, 12, 10, 52); // headboard
  disc(ctx, WOOD[3], 7, 12, 5);
  beam(ctx, 116, 26, 10, 38); // footboard
  disc(ctx, WOOD[3], 121, 26, 5);
  const straw = ["#9a8e70", "#b8ad94", "#d8cfb8", "#ebe3cc"];
  pixels(ctx, 104, 12, (x, y) => tone(straw, 0.75 - y * 0.05 + (x % 13 === 0 ? -0.3 : 0), x, y), 12, 32); // mattress
  pixels(ctx, 26, 10, (x, y) => {
    const e = Math.abs(x + 0.5 - 13) / 13 + Math.abs(y + 0.5 - 5) / 10;
    return e > 1.05 ? null : tone(["#c8c0a8", "#e0d8c2", "#f0ead8", "#fffaf0"], 1 - e * 0.7, x, y);
  }, 14, 23); // pillow
  const blue = ["#1f3e5c", "#2c5478", "#3d6e9a", "#5a8cba", "#7aa8d2"];
  pixels(ctx, 72, 16, (x, y) => {
    if (x < 5) return tone(blue, 0.85 - x * 0.12, x, y); // its turned-down edge
    let k = 0.55 - y * 0.03 + Math.sin(x * 0.35) * 0.08;
    if (y === 0) k = 0.9;
    return tone(blue, k, x, y);
  }, 44, 28);
  beam(ctx, 2, 44, 124, 8); // frame
  beam(ctx, 6, 52, 6, 12); // legs
  beam(ctx, 116, 52, 6, 12);
}

/** A shut door, 1x4: planks in an iron frame, with a little window and a brass handle. */
function paintDoor(ctx: Ctx): void {
  planks(ctx, 4, 4, 24, 124, 8, true, 100);
  plate(ctx, 0, 0, 4, 128); // frame
  plate(ctx, 28, 0, 4, 128);
  plate(ctx, 0, 0, 32, 4);
  for (const y of [28, 104]) {
    plate(ctx, 4, y, 24, 4); // iron bands
    rivet(ctx, 7, y + 1);
    rivet(ctx, 22, y + 1);
  }
  panel(ctx, 7, 40, 18, 18, IRON[1], IRON[0], IRON[3]); // window
  glassPane(ctx, 9, 42, 14, 14);
  brass(ctx, 22, 72, 4, 9); // handle
  fill(ctx, BRASS[4], 22, 72, 1, 1);
}

/** An open door, 1x4: swung back flat against its frame, leaving a dark way through. */
function paintDoorOpen(ctx: Ctx): void {
  opening(ctx, 32, 128);
  plate(ctx, 0, 0, 4, 128);
  plate(ctx, 28, 0, 4, 128);
  plate(ctx, 0, 0, 32, 4);
  beam(ctx, 4, 4, 6, 124); // the door, edge on
}

/** A cargo crate, 1x1: a braced timber box with iron corners. */
function paintCrate(ctx: Ctx): void {
  backWall(ctx);
  planks(ctx, 2, 6, 28, 26, 6, false, 110, ["#4a2f17", "#6e4622", "#9a6a3a", "#b07c48", "#c89458"]);
  beam(ctx, 2, 6, 28, 4); // battens
  beam(ctx, 2, 28, 28, 4);
  line(ctx, WOOD[0], 5, 28, 27, 10, 4); // brace
  line(ctx, WOOD[2], 5, 27, 26, 10, 2);
  for (const [x, y] of [[2, 6], [26, 6], [2, 28], [26, 28]] as const) {
    plate(ctx, x, y, 4, 4);
    fill(ctx, IRON[4], x + 1, y + 1, 1, 1);
  }
}

/** A shut hatch laid flat, 4x2: planks across in an iron frame, with a ring to pull. */
function paintHatchFlat(ctx: Ctx): void {
  planks(ctx, 0, 0, 128, 64, 13, false, 126);
  plate(ctx, 0, 0, 128, 6); // frame
  plate(ctx, 0, 58, 128, 6);
  plate(ctx, 0, 0, 6, 64);
  plate(ctx, 122, 0, 6, 64);
  for (const x of [28, 94]) {
    plate(ctx, x, 6, 6, 52); // iron bands
    for (const y of [10, 30, 50]) rivet(ctx, x + 2, y);
  }
  ring(ctx, BRASS[0], 64, 32, 5, 10); // pull ring
  ring(ctx, BRASS[2], 64, 32, 6, 9);
  shape(ctx, BRASS[4], 54, 22, 64, 32, (x, y) => Math.hypot(x - 64, y - 32) > 7 && Math.hypot(x - 64, y - 32) < 8.5);
}

/** An open flat hatch, 4x2: the lid swung up against the frame, a dark way through. */
function paintHatchFlatOpen(ctx: Ctx): void {
  opening(ctx, 128, 64);
  plate(ctx, 0, 0, 128, 6);
  plate(ctx, 0, 58, 128, 6);
  plate(ctx, 0, 0, 6, 64);
  plate(ctx, 122, 0, 6, 64);
  beam(ctx, 6, 6, 116, 6); // the lid, edge on
  fill(ctx, "#0b0805", 6, 12, 116, 3);
}

export type Paint = (ctx: Ctx, variant: number) => void;

/** Ship machinery and building blocks (IRONCLAD_FURNACE, IRONCLAD_PLANK ...) and plant blocks (NATURAL_REEDS ...): one in each family or look. */
type FamilyKey = Extract<BlockKey, `${Uppercase<Family>}_${string}`>;
type PlantKey = Extract<BlockKey, `${Uppercase<PlantLook>}_${string}`>;
/** Natural ground: rock, its layers and ores, and the ground covers (see groundArt). */
type GroundKey = "ROCK" | "REGOLITH" | "DEEP_ROCK" | "ORE" | "COAL" | "ICE" | "SUNGLASS" | "EMBER_ORE" | "GRASS" | "MOSS" | "LICHEN" | "FROST" | "ASH" | "CLAY" | "SAND" | "OBSIDIAN" | "MYCELIUM" | "SALT";

/**
 * The client half of the block registry (see shared/src/blocks.ts): how every
 * block is painted, by the same names. Pieces are painted whole (w x h tiles,
 * each tile showing its slice). A block's shape (see shapes.ts) isn't painted:
 * the mesh cuts the whole block's art to the slope. Every block must have one
 * (null: nothing to draw), so a new block can't be forgotten here. Whatever is
 * drawn over a clear background gets a dark outline (see outline).
 */
const PAINT: Record<BlockKey, Paint | null> = {
  EMPTY: null,
  // Drawn by its tree's sprite (see Trees), never as a tile.
  TREE: null,
  // Ship machinery and building blocks in every family, and plants in every look (see shipArt, buildArt, plantArt; that none is missing is checked below).
  ...(SHIP_PAINT as Record<FamilyKey, Paint>),
  ...(BUILD_PAINT as Record<FamilyKey, Paint>),
  ...(PLANT_PAINT as Record<PlantKey, Paint>),
  ...(GROUND_PAINT as Record<GroundKey, Paint>),
  WORKBENCH: paintWorkbench,
  HATCH: paintHatch,
  HATCH_OPEN: paintHatchOpen,
  CHEST: paintChest,
  BED: paintBed,
  DOOR: paintDoor,
  DOOR_OPEN: paintDoorOpen,
  CRATE: paintCrate,
  HATCH_FLAT: paintHatchFlat,
  HATCH_FLAT_OPEN: paintHatchFlatOpen,
  WATER: (ctx) => {
    const water = ["#1e4f96", "#2a64b4", "#3a7fd0", "#5a9ce0", "#8cc4f0"];
    pixels(ctx, S, S, (x, y) => {
      const r = Math.sin((y + noise(x, y, 8, 3) * 6) * TURN * 4);
      if (r > 0.95) return water[4]!;
      return tone(water, 0.5 + (noise(x, y, 16, 4) - 0.5) * 0.4 + (r > 0.75 ? 0.2 : 0), x, y);
    });
  },
  // A glow bulb in an iron-framed glass case, hung from a hook (GridMesh adds the glow).
  LANTERN: (ctx, v) => {
    backWall(ctx, v);
    plate(ctx, 14, 0, 4, 4); // hook
    plate(ctx, 8, 4, 16, 4); // cap
    glassPane(ctx, 8, 8, 16, 18);
    disc(ctx, "#ff9a2a", 16, 17, 6.5);
    disc(ctx, "#ffd27a", 16, 17, 4.5);
    fill(ctx, "#fff4d0", 14, 14, 3, 3);
    plate(ctx, 8, 8, 2, 18); // frame
    plate(ctx, 22, 8, 2, 18);
    panel(ctx, 6, 26, 20, 4, IRON[2], IRON[3], IRON[0]); // base
    fill(ctx, IRON[1], 9, 30, 14, 2);
  },
  // A lantern behind a lens off something from outside: an iron collar holding a wide glass lens over the bulb.
  LENS_LAMP: (ctx, v) => {
    backWall(ctx, v);
    plate(ctx, 14, 0, 4, 3); // hook
    plate(ctx, 7, 3, 18, 4); // cap
    glassPane(ctx, 8, 7, 16, 19);
    disc(ctx, "#ff9a2a", 16, 16, 5);
    disc(ctx, "#ffd27a", 16, 16, 3.5);
    // The lens: a dark iron ring, pale cold glass, a glint top left.
    ring(ctx, IRON[0], 16, 16, 8.5, 10.5);
    ring(ctx, IRON[2], 16, 16, 8.5, 9.4);
    ring(ctx, "#9fd8e0", 16, 16, 7, 8.5);
    fill(ctx, "#f2fbff", 11, 10, 3, 2);
    fill(ctx, "#cdeef4", 10, 12, 2, 2);
    for (const [x, y] of [[16, 5], [26, 16], [16, 27], [6, 16]] as const) fill(ctx, IRON[3], x - 1, y - 1, 2, 2); // rivets
    panel(ctx, 7, 27, 18, 4, IRON[2], IRON[3], IRON[0]); // base
  },
  // A warden's core: a riveted iron casing round a cold teal heart, lenses at its corners (GridMesh adds the glow).
  CORE: (ctx, v) => {
    backWall(ctx, v);
    panel(ctx, 4, 4, 24, 24, IRON[1], IRON[2], IRON[0]);
    panel(ctx, 7, 7, 18, 18, IRON[0], IRON[1], IRON[0]);
    disc(ctx, "#123038", 16, 16, 7);
    disc(ctx, "#2a8a9a", 16, 16, 5.5);
    disc(ctx, "#8ff0ff", 16, 16, 3.5);
    fill(ctx, "#eaffff", 14, 14, 2, 2);
    for (const [x, y] of [[6, 6], [24, 6], [6, 24], [24, 24]] as const) {
      disc(ctx, IRON[0], x + 1, y + 1, 2.5);
      disc(ctx, "#5fd8e8", x + 1, y + 1, 1.3);
    }
  },
  // A board on a post, with a few lines of writing (the words are the server's).
  SIGN: (ctx, v) => {
    backWall(ctx, v);
    beam(ctx, 14, 22, 4, 10); // post
    panel(ctx, 2, 4, 28, 19, WOOD[1], WOOD[3], WOOD[0]);
    planks(ctx, 4, 6, 24, 15, 5, false, 88);
    for (const [y, w] of [[8, 18], [12, 14], [16, 16]] as const) fill(ctx, "#3a2614", 7, y, w, 2);
  },
  PLANTER: (ctx) => {
    line(ctx, TEAL[3], 9, 19, 6, 6, 2); // fronds
    line(ctx, TEAL[4], 6, 6, 5, 3, 2);
    line(ctx, TEAL[2], 13, 19, 12, 9, 2);
    line(ctx, TEAL[3], 23, 19, 25, 7, 2);
    line(ctx, TEAL[5], 25, 7, 26, 4, 2);
    fill(ctx, "#d8cfb8", 17, 10, 2, 9); // a puffcap
    shape(ctx, "#a33a8c", 12, 4, 24, 11, (x, y) => (x - 18) ** 2 / 36 + (y - 11) ** 2 / 36 <= 1 && y < 11);
    fill(ctx, "#f2b8e6", 15, 6, 2, 2);
    fill(ctx, "#f2b8e6", 20, 7, 1, 1);
    beam(ctx, 2, 18, 28, 4, ["#4a2f17", "#7a4e26", "#a8733f", "#c48a4e", "#dca468"]); // rim
    fill(ctx, "#2a1c10", 4, 22, 24, 2); // soil
    planks(ctx, 4, 22, 24, 10, 5, false, 39);
  },
  TABLE: (ctx) => {
    panel(ctx, 17, 6, 7, 6, IRON[2], IRON[3], IRON[1]); // a mug on it
    ring(ctx, IRON[2], 24, 9, 1.2, 2.6);
    beam(ctx, 0, 12, S, 4); // top
    fill(ctx, WOOD[1], 0, 16, S, 2);
    beam(ctx, 4, 18, 4, 14); // legs
    beam(ctx, 24, 18, 4, 14);
  },
  STOOL: (ctx) => {
    beam(ctx, 6, 18, 20, 4); // seat
    fill(ctx, WOOD[0], 7, 22, 18, 1);
    beam(ctx, 8, 23, 4, 9); // legs
    beam(ctx, 20, 23, 4, 9);
    beam(ctx, 12, 27, 8, 2); // rung
  },
  FLAG: (ctx) => {
    pixels(ctx, 4, 29, (x) => IRON[[3, 4, 2, 1][x]!]!, 4, 3); // pole
    disc(ctx, BRASS[2], 6, 2.5, 2.5);
    fill(ctx, BRASS[4], 5, 1, 1, 1);
    panel(ctx, 2, 30, 8, 2, IRON[1], IRON[2], IRON[0]);
    const cloth = ["#5a1a14", "#8a2a22", "#b03a30", "#cc5a48"];
    pixels(ctx, 23, 20, (x, y) => {
      const wave = Math.round(Math.sin(x * 0.45) * 1.5);
      const top = 2 + wave;
      if (y < top || y >= top + 13) return null;
      if (y - top === 6 || y - top === 7) return x % 2 && y - top === 7 ? "#c8b890" : "#e8d8b0";
      return tone(cloth, 0.5 + Math.cos(x * 0.45) * 0.3, x, y);
    }, 8, 2);
  },
  // Living rock: ridged, creased bark (its grain runs along the surface), pale sapwood under it, ringed heartwood at the core.
  BARK: (ctx, v) => {
    const bark = ["#24170e", "#33241a", "#4a3322", "#5c4029", "#6e4f33"];
    pixels(ctx, S, S, (x0, y) => {
      const x = v % 2 ? S - 1 - x0 : x0;
      if (y < 5) return tone(["#5c4029", "#6b4a2e", "#7d5838", "#906844"], 0.85 - y * 0.15 + (noise(x, y, 4, 78) - 0.5) * 0.3, x, y);
      if (y === 5) return "#1e140c";
      const crease = Math.sin(y * TURN * 4 + noise(x, y, 8, 79 + v) * 4);
      if (crease > 0.88) return bark[0]!;
      return tone(bark, 0.5 + (crease < -0.4 ? 0.2 : 0) + (noise(x, y, 4, 80) - 0.5) * 0.3, x, y);
    });
  },
  SAPWOOD: (ctx, v) => {
    const sap = ["#7a5a36", "#94704a", "#b08a5a", "#c49e6c", "#d4b07e"];
    pixels(ctx, S, S, (x, y) => tone(sap, 0.55 + grain(x, y, 83 + v * 53) * 1.2 + (noise(x, y, 8, 84 + v) - 0.5) * 0.25, x, y, 0.5));
  },
  HEARTWOOD: (ctx, v) => {
    const heart = ["#3e1a10", "#521f14", "#6a2e1e", "#8a4230", "#a05a40"];
    const [cx, cy] = [v === 1 ? 8 : 24, v === 2 ? 24 : 8];
    pixels(ctx, S, S, (x, y) => {
      const r = Math.sin(Math.hypot(x + 0.5 - cx, y + 0.5 - cy) * 1.1 + noise(x, y, 8, 85) * 2.5);
      return tone(heart, 0.45 + (r > 0.7 ? 0.35 : r < -0.8 ? -0.2 : 0) + (noise(x, y, 4, 86) - 0.5) * 0.15, x, y, 0.4);
    });
  },
  BRANCH: (ctx, v) => {
    const wood = ["#2e2014", "#43301f", "#5a3e28", "#6e4c31", "#80603e"];
    pixels(ctx, S, S, (x, y) => {
      const g = (x + v * 3) % 8;
      if (g === 0) return wood[0]!;
      return tone(wood, 0.5 + (g === 1 ? 0.25 : g === 7 ? -0.15 : 0) + grain(y, x, 89 + v) + (noise(x, y, 8, 90 + v) - 0.5) * 0.2, x, y, 0.5);
    });
  },
  FOLIAGE: (ctx, v) => pixels(ctx, S, S, leafClumps(v, [[4, 24, 6], [28, 24, 6], [8, 10, 8], [22, 8, 8], [16, 22, 10]], ["#1d4419", "#2d6324", "#3f8430", "#5fa042", "#8acb5a"], 97 + v)),
  // A bead of amber sap, glowing, in the wood.
  SAP: (ctx) => {
    pixels(ctx, S, S, (x, y) => tone(["#7a5a36", "#94704a", "#b08a5a", "#c49e6c"], 0.55 + grain(x, y, 87) * 1.2, x, y));
    pixels(ctx, S, S, (x, y) => {
      const [u, w] = [(x + 0.5 - 16) * Math.cos(0.4) + (y + 0.5 - 16) * Math.sin(0.4), -(x + 0.5 - 16) * Math.sin(0.4) + (y + 0.5 - 16) * Math.cos(0.4)];
      const d = (u / 12) ** 2 + (w / 10) ** 2;
      if (d > 1) return null;
      return d > 0.82 ? "#6a4208" : tone(["#8a5a10", "#c07e14", "#e0a020", "#f0c040", "#ffe08a"], 0.95 - d * 0.5 - (u + w) / 40, x, y);
    });
    fill(ctx, "#fff4c0", 10, 11, 4, 2);
  },
  // The heart of a living rock: a red orb in the heartwood, beating (the renderer pulses its glow).
  HEART: (ctx) => {
    fill(ctx, "#521f14");
    pixels(ctx, S, S, (x, y) => (Math.sin(Math.hypot(x - 16, y - 16) * 1.1) > 0.7 ? "#6a2e1e" : null));
    pixels(ctx, S, S, (x, y) => {
      const dx = (x + 0.5 - 16) / 12;
      const dy = (y + 0.5 - 16) / 12;
      const d2 = dx * dx + dy * dy;
      if (d2 > 1) return null;
      return d2 > 0.8 ? "#3e0a10" : tone(["#6a1018", "#9a2028", "#c0303a", "#e05a5a", "#ff8a8a"], 0.75 - dx * 0.3 - dy * 0.3 + (d2 < 0.2 ? 0.25 : 0), x, y);
    });
    fill(ctx, "#ffd0d0", 11, 10, 3, 2);
  },
  HUSK: (ctx, v) => {
    const husk = ["#3e3c39", "#55524e", "#6e6a64", "#87837c", "#a29e96"];
    pixels(ctx, S, S, (x, y) => {
      if (y < 5) return tone(husk, 0.85 - y * 0.08, x, y);
      if (y === 5) return husk[0]!;
      const crease = Math.sin(y * TURN * 4 + noise(x, y, 8, 101 + v) * 3);
      if (crease > 0.9) return husk[0]!;
      return tone(husk, 0.45 + (crease < -0.5 ? 0.15 : 0) + (noise(x, y, 4, 102 + v) - 0.5) * 0.3, x, y);
    });
  },
};
for (const key of BLOCK_KEYS) if (PAINT[key] === undefined) throw new Error(`no painter for ${key}`);

/**
 * Blocks that glow (glow bulbs, void crystals, lanterns): an HDR colour
 * (above 1, so it blooms; the renderer pulses it) over the tile, inset from
 * its edges.
 */
/** The same thing for a plant kind in every look (NATURAL_x, OTHERWORLD_x, WOODCUT_x). */
const inEveryLook = <V>(kind: string, value: V) => Object.fromEntries(PLANT_LOOKS.map((look) => [`${look.toUpperCase()}_${kind}`, value])) as Partial<Record<BlockKey, V>>;

export const GLOW: Partial<Record<BlockKey, { color: [number, number, number]; inset: number }>> = {
  ...inEveryLook("CRYSTAL", { color: [1.3, 0.7, 2.4], inset: 0.32 }),
  ...inEveryLook("GLOW_FUNGUS", { color: [0.5, 1.8, 2.6], inset: 0.3 }),
  ...inEveryLook("CINDER_BLOOM", { color: [2.6, 1.1, 0.3], inset: 0.3 }),
  ...inEveryLook("GLOW_DROPS", { color: [0.6, 2.2, 1.8], inset: 0.3 }),
  // The bulb in its glass case (matches its painting), warmer than one on the tree.
  LANTERN: { color: [3.0, 1.9, 0.8], inset: 0.36 },
  LENS_LAMP: { color: [3.4, 2.3, 1.1], inset: 0.38 },
  CORE: { color: [0.5, 2.0, 2.8], inset: 0.36 },
  EMBER_ORE: { color: [2.6, 1.0, 0.3], inset: 0.3 },
  SAP: { color: [2.2, 1.5, 0.3], inset: 0.3 },
  HEART: { color: [2.8, 0.6, 0.7], inset: 0.2 },
};

/** Packed ABGR (canvas ImageData on little-endian machines). */
const rgba = (r: number, g: number, b: number) => (255 << 24) | (b << 16) | (g << 8) | r;
/** Anything built shows on the map in one timber colour. */
const BUILT_ON_MAP = rgba(168, 115, 63);

/** How natural blocks show on the map (see MAP_COLOR). */
const MAP: Partial<Record<BlockKey, number>> = {
  ROCK: rgba(92, 86, 80),
  ORE: rgba(150, 108, 70),
  COAL: rgba(40, 38, 36),
  GRASS: rgba(80, 150, 95),
  TREE: rgba(60, 120, 50),
  WATER: rgba(50, 110, 190),
  ICE: rgba(190, 224, 238),
  REGOLITH: rgba(133, 118, 99),
  DEEP_ROCK: rgba(70, 63, 56),
  SUNGLASS: rgba(150, 100, 60),
  EMBER_ORE: rgba(230, 110, 40),
  MOSS: rgba(70, 97, 58),
  LICHEN: rgba(160, 150, 90),
  FROST: rgba(225, 240, 250),
  ASH: rgba(90, 84, 80),
  BARK: rgba(107, 74, 46),
  SAPWOOD: rgba(176, 138, 90),
  HEARTWOOD: rgba(106, 46, 30),
  BRANCH: rgba(90, 62, 40),
  FOLIAGE: rgba(63, 122, 46),
  SAP: rgba(224, 160, 32),
  HEART: rgba(200, 50, 60),
  HUSK: rgba(110, 106, 100),
  CLAY: rgba(138, 110, 86),
  SAND: rgba(210, 192, 142),
  OBSIDIAN: rgba(30, 26, 40),
  MYCELIUM: rgba(138, 132, 160),
  SALT: rgba(232, 224, 207),
  ...inEveryLook("REEDS", rgba(90, 138, 58)),
  ...inEveryLook("SHRUB", rgba(94, 106, 58)),
  ...inEveryLook("GLOW_FUNGUS", rgba(58, 160, 200)),
  ...inEveryLook("FROST_FLOWER", rgba(238, 248, 255)),
  ...inEveryLook("ROOTS", rgba(110, 76, 49)),
  ...inEveryLook("HANGING_VINE", rgba(63, 106, 46)),
  ...inEveryLook("CRYSTAL", rgba(170, 120, 255)),
  ...inEveryLook("SAPLING", rgba(60, 190, 160)),
  ...inEveryLook("THISTLE", rgba(140, 80, 190)),
  ...inEveryLook("FERN", rgba(70, 130, 60)),
  ...inEveryLook("FROST_GRASS", rgba(200, 225, 240)),
  ...inEveryLook("CINDER_BLOOM", rgba(230, 100, 40)),
  ...inEveryLook("LICHEN_FINGERS", rgba(170, 160, 110)),
  ...inEveryLook("GLOW_DROPS", rgba(90, 200, 180)),
};

/** Each tile's colour on the minimap (packed ABGR), by tile id. */
export const MAP_COLOR: readonly number[] = TILES.map((def) => MAP[def.key] ?? BUILT_ON_MAP);

// ----- the atlas


/** A block's whole art (all of a piece's tiles), variant `v`, outlined. */
function blockArt(key: BlockKey, v: number): HTMLCanvasElement {
  const piece = BLOCKS[key].piece;
  const canvas = document.createElement("canvas");
  canvas.width = S * (piece?.w ?? 1);
  canvas.height = S * (piece?.h ?? 1);
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.imageSmoothingEnabled = false;
  PAINT[key]!(ctx, v);
  outline(canvas);
  return canvas;
}

/**
 * Each tile has a slot in the atlas: the tile with GUTTER pixels round it,
 * copies of its edge, so blending between texels (and between mip levels)
 * never reaches the next slot. Slots run COLS to a row, a tile's variants
 * one after another.
 */
const GUTTER = 8;
const SLOT = S + GUTTER * 2;
const COLS = 32;
const ROWS = Math.ceil((TILES.length * VARIANTS) / COLS);
/** Mip levels made: down to tiles S >> (MIPS - 1) pixels across, the last whose gutter is still a whole pixel. */
const MIPS = 4;
const slotOf = (id: TileId, variant: number) => id * VARIANTS + variant;

/** Every tile's art at full size, S x S RGBA, by slot (none where a tile isn't drawn). */
const tileArt: Uint8ClampedArray[] = (() => {
  const out: Uint8ClampedArray[] = [];
  for (const key of BLOCK_KEYS) {
    if (!PAINT[key]) continue;
    const b = BLOCKS[key];
    if (b.piece) {
      // Painted once whole; each tile shows its slice (rows counted up from the bottom), the backed anchor like the plain one.
      const { w, h } = b.piece;
      const ctx = blockArt(key, 0).getContext("2d", { willReadFrequently: true })!;
      for (let i = 0; i <= w * h; i++) {
        const k = i < w * h ? i : 0;
        const art = ctx.getImageData((k % w) * S, (h - 1 - Math.floor(k / w)) * S, S, S).data;
        for (let v = 0; v < VARIANTS; v++) out[slotOf(b.id + i, v)] = art;
      }
    } else {
      for (let v = 0; v < VARIANTS; v++) out[slotOf(b.id, v)] = blockArt(key, v).getContext("2d", { willReadFrequently: true })!.getImageData(0, 0, S, S).data;
    }
  }
  return out;
})();

const LINEAR = Float32Array.from({ length: 256 }, (_, c) => (c / 255 <= 0.04045 ? c / 255 / 12.92 : ((c / 255 + 0.055) / 1.055) ** 2.4));
const toSrgb = (l: number) => 255 * (l <= 0.0031308 ? l * 12.92 : 1.055 * l ** (1 / 2.4) - 0.055);

/** A t x t tile at half size: each 2x2 averaged in linear light, weighted by how opaque each is. */
function halve(d: Uint8ClampedArray, t: number): Uint8ClampedArray {
  const h = t / 2;
  const out = new Uint8ClampedArray(h * h * 4);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < h; x++) {
      let a = 0;
      const sum = [0, 0, 0];
      for (const i of [(y * 2 * t + x * 2) * 4, (y * 2 * t + x * 2 + 1) * 4, ((y * 2 + 1) * t + x * 2) * 4, ((y * 2 + 1) * t + x * 2 + 1) * 4]) {
        const w = d[i + 3]!;
        a += w;
        for (let c = 0; c < 3; c++) sum[c]! += LINEAR[d[i + c]!]! * w;
      }
      const j = (y * h + x) * 4;
      if (a) for (let c = 0; c < 3; c++) out[j + c] = toSrgb(sum[c]! / a);
      out[j + 3] = a / 4;
    }
  }
  return out;
}

/** The atlas at each mip level: every tile in its slot, halved `level` times, its gutter round it. */
const atlasLevels: HTMLCanvasElement[] = (() => {
  const levels: HTMLCanvasElement[] = [];
  let art = tileArt;
  for (let level = 0; level < MIPS; level++) {
    if (level) art = art.map((d) => halve(d, S >> (level - 1)));
    const [t, g, s] = [S >> level, GUTTER >> level, SLOT >> level];
    const canvas = document.createElement("canvas");
    canvas.width = COLS * s;
    canvas.height = ROWS * s;
    const ctx = canvas.getContext("2d")!;
    const img = ctx.createImageData(canvas.width, canvas.height);
    const out = new Uint32Array(img.data.buffer);
    art.forEach((d, slot) => {
      const tile = new Uint32Array(d.buffer, d.byteOffset, t * t);
      const [ox, oy] = [(slot % COLS) * s, Math.floor(slot / COLS) * s];
      for (let y = 0; y < s; y++) {
        const ty = Math.min(t - 1, Math.max(0, y - g));
        for (let x = 0; x < s; x++) out[(oy + y) * canvas.width + ox + x] = tile[ty * t + Math.min(t - 1, Math.max(0, x - g))]!;
      }
    });
    ctx.putImageData(img, 0, 0);
    levels.push(canvas);
  }
  return levels;
})();

/** The atlas at full size (see atlasLevels). */
export const atlasCanvas: HTMLCanvasElement = atlasLevels[0]!;

/**
 * Close up, texels are sharp squares (nearest). Further out, where a tile is
 * fewer screen pixels than texels, it's blended from the mip levels, so the
 * art doesn't shimmer and crawl as the camera moves.
 */
export const atlasTexture: THREE.CanvasTexture = (() => {
  const tex = new THREE.CanvasTexture(atlasCanvas);
  tex.mipmaps = atlasLevels;
  tex.generateMipmaps = false;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
})();

/** Where variant `variant` of a tile is in the atlas canvas (its top left, in pixels, inside the gutter). */
function slotCorner(id: TileId, variant: number): [number, number] {
  const slot = slotOf(id, variant);
  return [(slot % COLS) * SLOT + GUTTER, Math.floor(slot / COLS) * SLOT + GUTTER];
}

/** UV rectangle of variant `variant` of a tile. Its gutter means nothing next to it bleeds in. */
export function tileUv(id: TileId, variant = 0): { u0: number; u1: number; v0: number; v1: number } {
  const [x, y] = slotCorner(id, variant);
  const w = atlasCanvas.width;
  const h = atlasCanvas.height;
  // Row 0 is at the top of the canvas, which is the top of the texture (v = 1).
  return { u0: x / w, u1: (x + S) / w, v0: 1 - (y + S) / h, v1: 1 - y / h };
}

/** Draw a tile into a 2D canvas (for the hotbar). */
export function drawTileIcon(id: TileId, canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(atlasCanvas, ...slotCorner(id, 0), S, S, 0, 0, canvas.width, canvas.height);
}

// ----- items with no tile of their own

/** Tool heads by what they're made of: [body, highlight]. */
const TIER_PAINT: Record<ToolTier, readonly [string, string]> = {
  wood: ["#9a6a36", "#c08a50"],
  stone: ["#80858c", "#a8adb2"],
  iron: ["#a6adb4", "#e4e9ee"],
};

/** A tool's handle, from its foot (x0, y0) up to (x1, y1): a dark side and a lit one. */
function handle(ctx: Ctx, x0: number, y0: number, x1: number, y1: number): void {
  line(ctx, WOOD[1], x0, y0, x1, y1, 4);
  line(ctx, WOOD[3], x0 - 1, y0 - 1, x1 - 1, y1 - 1, 1.5);
}

/** 32 x 32 pixel art for tools, which have no tile of their own, with heads in `head`'s colours. */
const TOOL_PAINTERS: Record<ToolKind, (ctx: Ctx, head: readonly [string, string]) => void> = {
  pickaxe: (ctx, [body, light]) => {
    handle(ctx, 6, 28, 20, 8);
    // The pick head arcs across the top of the handle.
    path(ctx, body, [5, 14, 8, 9, 13, 6, 19, 5, 24, 6, 28, 9, 29, 14], 4);
    path(ctx, light, [7, 10, 12, 6.5, 19, 5.5, 24, 6.5], 1.5);
    fill(ctx, WOOD[0], 18, 6, 4, 4);
  },
  axe: (ctx, [body, light]) => {
    handle(ctx, 6, 29, 21, 6);
    poly(ctx, body, [17, 3, 24, 3, 30, 6, 30, 15, 24, 13, 20, 10]);
    line(ctx, light, 29, 6, 29, 14, 1.5);
    line(ctx, light, 18, 3.5, 24, 3.5, 1);
  },
  shovel: (ctx, [body, light]) => {
    handle(ctx, 4, 29, 18, 12);
    poly(ctx, body, [16, 9, 22, 2, 29, 3, 30, 10, 23, 16]);
    line(ctx, light, 22, 3, 28, 4, 1.5);
    line(ctx, light, 21, 4, 18, 9, 1);
  },
  sword: (ctx, [body, light]) => {
    line(ctx, body, 11, 21, 28, 4, 4);
    line(ctx, light, 11, 20, 27, 4, 1.5);
    line(ctx, WOOD[1], 5, 15, 15, 25, 3); // cross guard, across the blade
    line(ctx, WOOD[2], 4, 28, 9, 23, 3); // grip
    disc(ctx, BRASS[2], 3.5, 28.5, 2.5);
  },
};

/** Wood: a little stack of cut boards. */
function paintWood(ctx: Ctx): void {
  for (const y of [6, 14, 22]) {
    beam(ctx, 4, y, 24, 6);
    disc(ctx, WOOD[4], 26.5, y + 3, 2.5); // end grain
    ring(ctx, WOOD[2], 26.5, y + 3, 0.6, 1.4);
  }
}

/** 32 x 32 pixel art for the other items with no tile of their own. */
const ITEM_PAINTERS: Partial<Record<ItemId, (ctx: Ctx) => void>> = {
  // A coil of rope, its end hanging loose.
  rope: (ctx) => {
    ring(ctx, ROPE[0], 16, 14, 3, 12.5);
    ring(ctx, ROPE[2], 16, 14, 4, 11.5);
    for (const r of [6.5, 9]) ring(ctx, ROPE[1], 16, 14, r, r + 0.9);
    shape(ctx, ROPE[3], 4, 2, 16, 14, (x, y) => Math.hypot(x - 16, y - 14) > 9.5 && Math.hypot(x - 16, y - 14) < 11);
    line(ctx, ROPE[2], 24, 22, 27, 30, 3); // the loose end
    line(ctx, ROPE[0], 27, 30, 28, 31, 2);
  },
  // An iron grappling hook on a line.
  grapple: (ctx) => {
    line(ctx, ROPE[1], 2, 30, 13, 20, 3);
    line(ctx, ROPE[3], 2, 29, 12, 20, 1);
    fill(ctx, "#a6adb4", 14, 6, 4, 13); // shank
    fill(ctx, "#e4e9ee", 14, 6, 1, 12);
    ring(ctx, "#80858c", 15, 20, 1.5, 3.5); // ring
    // Three prongs curling back from the top.
    path(ctx, "#a6adb4", [16, 6, 8, 7, 5, 13], 3);
    path(ctx, "#a6adb4", [16, 6, 24, 7, 27, 13], 3);
    line(ctx, "#a6adb4", 16, 2, 16, 7, 3);
    line(ctx, "#e4e9ee", 8, 6, 14, 5.5, 1);
  },
  // A patch kit: a spare plank, a bundle of nails, tied up with fibre.
  patchKit: (ctx) => {
    beam(ctx, 2, 12, 28, 10);
    disc(ctx, WOOD[4], 28.5, 17, 3);
    fill(ctx, "#3d6e2c", 10, 10, 4, 14); // the tie
    fill(ctx, "#5a9a46", 10, 10, 2, 14);
    for (const x of [18, 22, 26]) {
      fill(ctx, "#a6adb4", x, 3, 2, 10); // nails, heads up
      fill(ctx, "#e4e9ee", x - 1, 2, 4, 2);
    }
  },
  // A skyfish: deep blue, a glowing stripe along its side, long fins trailing.
  skyfish: (ctx) => {
    pixels(ctx, 28, 14, (x, y) => {
      const dx = (x + 0.5 - 16) / 11;
      const dy = (y + 0.5 - 7) / 5.5;
      if (dx * dx + dy * dy > 1) return null;
      if (Math.abs(y + 0.5 - 7.5) < 0.8 && x > 6) return "#9ff8ff";
      return y < 7 ? (y < 4 ? "#1d2a63" : "#2d4f95") : "#5fb4d6";
    }, 2, 9);
    // The tail, forked.
    for (let i = 0; i < 6; i++) {
      fill(ctx, "#4a7fd0", 7 - i, 15 - i, 2, 1);
      fill(ctx, "#4a7fd0", 7 - i, 16 + i, 2, 1);
    }
    // A sail of a fin on its back.
    for (let i = 0; i < 5; i++) fill(ctx, "#6a5fd8", 12 + i, 10 - i, 7 - i, 1);
    fill(ctx, "#e8ffff", 24, 14, 2, 2); // the eye
  },
  // A salted skyfish: the fish, crusted white with salt.
  saltFish: (ctx) => {
    ITEM_PAINTERS.skyfish!(ctx);
    for (const [x, y] of [[9, 11], [13, 13], [16, 10], [19, 14], [22, 12], [11, 17], [15, 18], [20, 17], [24, 16], [17, 15]] as const) fill(ctx, y % 2 ? "#f4efe4" : "#ffffff", x, y, 2, 1);
  },
  // Scrap: a bent riveted plate and a twisted strut, off something from outside.
  scrap: (ctx) => {
    path(ctx, IRON[1], [4, 22, 12, 12, 22, 14, 27, 6], 6);
    path(ctx, IRON[3], [4, 20, 12, 10, 22, 12, 27, 4], 1.5);
    for (const [x, y] of [[9, 15], [16, 12], [23, 10]] as const) {
      fill(ctx, IRON[0], x, y, 2, 2);
      fill(ctx, IRON[4], x, y, 1, 1);
    }
    line(ctx, IRON[2], 8, 29, 20, 21, 3);
    line(ctx, "#5fd8e8", 21, 21, 23, 19, 1.5); // a lens chip, still glinting
  },
  // Wire: a tight coil of cold drawn wire, its end sprung loose.
  wire: (ctx) => {
    for (const r of [5, 7.5, 10, 12.5]) ring(ctx, r % 5 ? "#8a949c" : "#c8d2da", 16, 15, r, r + 1);
    line(ctx, "#c8d2da", 26, 22, 30, 30, 1.5);
    line(ctx, "#9fe8f0", 29, 29, 30, 30, 1);
  },
  // A lens: thick ground glass in an iron ring, a cold glint across it.
  lens: (ctx) => {
    ring(ctx, IRON[0], 16, 16, 11, 14);
    ring(ctx, IRON[2], 16, 16, 11, 12);
    pixels(ctx, 22, 22, (x, y) => {
      const dx = (x + 0.5 - 11) / 11;
      const dy = (y + 0.5 - 11) / 11;
      if (dx * dx + dy * dy > 1) return null;
      return tone(["#2a5a66", "#4a8e9a", "#7cc4cc", "#bdeef2"], 0.75 - dx * 0.35 - dy * 0.35, x, y);
    }, 5, 5);
    fill(ctx, "#ffffff", 10, 9, 4, 2);
    fill(ctx, "#e4fbff", 9, 11, 2, 2);
  },
  // A cold cell: a sealed iron canister with a dead lens window, banded.
  coldCell: (ctx) => {
    panel(ctx, 9, 4, 14, 24, IRON[1], IRON[2], IRON[0]);
    fill(ctx, IRON[3], 8, 7, 16, 2);
    fill(ctx, IRON[3], 8, 23, 16, 2);
    disc(ctx, IRON[0], 16, 16, 4.5);
    disc(ctx, "#1c3a42", 16, 16, 3.2);
    fill(ctx, "#4a7a84", 14, 14, 2, 1);
    fill(ctx, IRON[4], 12, 2, 8, 2); // cap
  },
  // A stoppered glass flask of amber whale oil.
  whaleOil: (ctx) => {
    beam(ctx, 12, 0, 8, 6); // cork
    glassPane(ctx, 12, 6, 8, 6); // neck
    pixels(ctx, 24, 24, (x, y) => {
      const dx = (x + 0.5 - 12) / 11.5;
      const dy = (y + 0.5 - 12) / 11.5;
      const d2 = dx * dx + dy * dy;
      if (d2 > 1) return null;
      if (y >= 11) return tone(["#8a5a10", "#b07818", "#d89a2a", "#f0c060"], y === 11 ? 1 : 0.8 - dx * 0.3 - (y - 11) * 0.03, x, y);
      return tone(GLASS, 0.85 - dx * 0.3 - dy * 0.2, x, y);
    }, 4, 9);
    fill(ctx, "#ffffff", 9, 13, 3, 2); // glint on the glass
  },
};

/** Draw an item's icon: its tile's art, or its own (tools, wood, pieces drawn whole). */
export function drawItemIcon(id: ItemId, canvas: HTMLCanvasElement): void {
  const def = ITEMS[id];
  const tile = iconTile(id);
  const key = tile === undefined ? undefined : TILES[tile]!.key;
  const piece = key && BLOCKS[key].piece;
  // A block (placed, or the natural one it's dug out of) looks like its tile; a piece is drawn whole.
  if (tile !== undefined && !piece) return drawTileIcon(tile, canvas);
  let art: HTMLCanvasElement;
  if (piece) art = blockArt(key!, 0);
  else {
    art = document.createElement("canvas");
    art.width = art.height = S;
    const ctx2 = art.getContext("2d", { willReadFrequently: true })!;
    if (def.tool && def.tier) TOOL_PAINTERS[def.tool](ctx2, TIER_PAINT[def.tier]);
    else (ITEM_PAINTERS[id] ?? paintWood)(ctx2);
    outline(art);
  }
  // Fit it in the middle of the icon, keeping its shape.
  const scale = Math.min(canvas.width / art.width, canvas.height / art.height);
  const w = art.width * scale;
  const h = art.height * scale;
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = scale < 1;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(art, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
}
